/**
 * Static + dynamic redirect map for the route-fork.
 *
 * Old URLs (legacy paths from pre-route-fork) map to their canonical
 * destination on the new four-route surface (`/admin`, `/distributor`,
 * `/portal`, `/store/:slug`).
 *
 * Entries are either:
 *   - **strings** — absolute paths starting with `/`. Vue Router
 *     forwards same-named params automatically (e.g. `/shop/:id` ->
 *     `/store/:id` carries `:id`).
 *   - **functions** — `(to) => string`. Used when the destination needs
 *     a value that isn't a same-named param of the source — most
 *     commonly the shopper's `:slug`, which legacy flat paths
 *     (`/cart`, `/orders`, ...) don't carry. The function reads
 *     `localStorage.storeUrl` (set on the shopper's first visit to
 *     `/store/:id`) and falls back to `/login` when no slug is known.
 *
 * `router/index.js` iterates the entries to produce
 * `{ path, redirect }` route definitions.
 *
 * Test guardrail: `__tests__/redirects.spec.js` +
 * `__tests__/storefront-routes-pr-r5.spec.js`.
 */

/**
 * Build a function-redirect that fills in the shopper's `:slug` from
 * `localStorage.storeUrl` (set on first visit to `/store/:id`).
 *
 * Slug resolution here intentionally does NOT consult `to.params`:
 * legacy source paths like `/orders/:id` carry the *order* id in
 * `params.id`, which would shadow the real slug. The shopper-side
 * `resolveStorefrontSlug` (router/storefrontSlug.js) is for in-page
 * navigation where `route.params.slug` and `route.params.id` (from
 * `/store/:id`) are the canonical slug source — that helper has the
 * opposite priority.
 *
 * When no slug resolves, send the shopper to `/login` so they can
 * re-establish a session and pick a store; landing them on `/shop`
 * (the bare landing) would be just as useless because they'd still
 * have no store context.
 */
function storefrontRedirect(builder) {
    return (to) => {
        const slug =
            typeof localStorage !== "undefined"
                ? localStorage.getItem("storeUrl")
                : null;
        if (!slug) {
            return "/login";
        }
        return builder(slug, to);
    };
}

export const REDIRECTS = {
    // NOTE (P5, Live Storefront ADR 0060/0061): the legacy
    // `/shop/:id` -> `/store/:id` redirect was REMOVED here. `/shop/<slug>`
    // is now the CANONICAL storefront Home surface (a real route in
    // router/groups/storefront.js: StorefrontSessionGate -> HomePage), not a
    // redirect into the legacy single page. P8 retired the legacy scrolling
    // page's own `/store/:id` entry (below); Phase D retired the bare
    // `/shop` entry (next). A `/shop/:id` redirect entry here would be a
    // dead shadow -- storefrontRoutes (which owns `/shop/:slug`) register
    // BEFORE redirectRoutes, so the real Home route always wins
    // `router.resolve`.

    // Phase D (Categories & Storefront, ADR 0059, card a7j1VV6D): the bare
    // `/shop` landing (generic_shop, StorefrontViewShopper) is retired --
    // it was the last live surface reading the legacy
    // campaign.product_categories string list. There is no store-less
    // canonical destination for a shopper with no slug context, so this
    // reuses the SAME `storefrontRedirect()` fallback the other flat
    // legacy paths above already use: resolve `localStorage.storeUrl` and
    // land on that store's canonical Home, or `/login` when no slug is
    // known (mirrors `postLoginRedirect()`'s documented "shopper with no
    // campaignSlug falls through to /login" rule -- there is nowhere
    // sensible to send a shopper with no store context).
    "/shop": storefrontRedirect((slug) => `/shop/${slug}`),

    // Convergence (card q7EQ3a0b): the canonical shopper SESSION tree moved
    // from `/store/:slug/*` to `/shop/:slug/*`. Two generations of legacy
    // links fold into the new canonical form:
    //
    //   1. The oldest FLAT paths (`/cart`, `/orders`, ...) carry no `:slug`,
    //      so each destination is a function that pulls the slug from
    //      localStorage (set by `/store/:id`) and builds the `/shop/...` URL.
    //   2. The intermediate `/store/:slug/*` session paths (added by PR-R5)
    //      carry `:slug` directly and are forwarded to the matching
    //      `/shop/:slug/*` route (the `/store/:slug/*` entries below).
    "/cart": storefrontRedirect((slug) => `/shop/${slug}/cart`),
    "/orders": storefrontRedirect((slug) => `/shop/${slug}/orders`),
    "/orders/:id": storefrontRedirect(
        (slug, to) => `/shop/${slug}/orders/${to.params.id}`
    ),
    "/order_confirmation": storefrontRedirect((slug, to) => {
        // The legacy /order_confirmation took ?orderNumber=<id> as a
        // query param. The new canonical URL puts the order id in the
        // path: /shop/:slug/orders/:id/confirmation. Forward the
        // orderNumber query if present so the destination still has it
        // (the confirmation component reads it from $route.query).
        const orderId = to.query?.orderNumber;
        const base = orderId
            ? `/shop/${slug}/orders/${orderId}/confirmation`
            : `/shop/${slug}/orders`;
        return { path: base, query: to.query };
    }),
    "/account/addresses": storefrontRedirect(
        (slug) => `/shop/${slug}/addresses`
    ),
    "/account/payment-methods": storefrontRedirect(
        (slug) => `/shop/${slug}/payment-methods`
    ),

    // P8 (storefront convergence, card q7EQ3a0b): retire the two legacy
    // shopper ENTRY surfaces and fold them into the canonical `/shop/<slug>`
    // tree.
    //
    //   1. `/store/:id` -- the legacy single-page storefront (custom_shop,
    //      StorefrontViewShopper). The `:id` segment is a slug OR a numeric
    //      store id; the `/shop` entry resolver (getCampaignIdFromShopperURL)
    //      accepts BOTH (#1888), so forwarding the segment straight through
    //      lands bookmarked `/store/13` on the right store's Home (a numeric
    //      URL is canonicalized to the slug URL inside the session gate). A
    //      function is required because the destination param name (`:slug`)
    //      differs from the source (`:id`) -- a plain string would not
    //      substitute.
    //   2. `/store/:slug/products[/:id]` -- the legacy FLAT gallery + detail
    //      (components since DELETED as dead code). The canonical product
    //      surfaces are the P5/P6 Category + Product pages; there is no flat
    //      gallery equivalent, so both redirect to the store's `/shop/<slug>`
    //      Home (the safe default -- the shopper re-enters the browse tree
    //      from Home). The `/products/:id` detail id is intentionally dropped
    //      (the legacy detail id is a campaign_product id with no stable
    //      mapping to the P6 `/c/<category>/p/<id>` URL, which also needs a
    //      category slug the flat URL never carried).
    //
    // Each returns the `{ path, query }` OBJECT form so the QUERY STRING is
    // preserved across the hop. This matters for `?preview=1`: the store-
    // editor preview drawer (StoreEditShell.vue) embeds an iframe at
    // `/store/<slug>?preview=1`, which now redirects here -- the new `/shop`
    // tree's StorefrontSessionGate reads `?preview=1` to skip the shopper
    // token swap (router/storefrontPreview.js). A plain string destination
    // would DROP the query and break same-origin preview.
    "/store/:id": (to) => ({ path: `/shop/${to.params.id}`, query: to.query }),
    "/store/:slug/products": (to) => ({
        path: `/shop/${to.params.slug}`,
        query: to.query,
    }),
    "/store/:slug/products/:id": (to) => ({
        path: `/shop/${to.params.slug}`,
        query: to.query,
    }),

    // Intermediate `/store/:slug/*` session paths -> `/shop/:slug/*`.
    // These carry `:slug` (and, for order routes, `:id`) as source params.
    // Each destination is a FUNCTION that reads `to.params` and builds a
    // concrete path -- a plain string redirect like `/shop/:slug/cart`
    // would forward LITERALLY (Vue Router does not substitute named params
    // into a raw string redirect target), so functions are required to
    // interpolate the resolved slug/id. Query is preserved on the
    // confirmation route (the confirmation page reads it from $route.query).
    "/store/:slug/signin": (to) => `/shop/${to.params.slug}/signin`,
    "/store/:slug/cart": (to) => `/shop/${to.params.slug}/cart`,
    "/store/:slug/orders": (to) => `/shop/${to.params.slug}/orders`,
    "/store/:slug/orders/:id": (to) =>
        `/shop/${to.params.slug}/orders/${to.params.id}`,
    "/store/:slug/orders/:id/confirmation": (to) => ({
        path: `/shop/${to.params.slug}/orders/${to.params.id}/confirmation`,
        query: to.query,
    }),
    "/store/:slug/addresses": (to) => `/shop/${to.params.slug}/addresses`,
    "/store/:slug/payment-methods": (to) =>
        `/shop/${to.params.slug}/payment-methods`,

    // NOTE: deep `/admin/*` Owner-URL compatibility redirects (campaigns,
    // vendors, products, setup, ...) are NOT defined here. They live as a
    // single source in the `legacyAdminRedirects` array in router/index.js,
    // which is the more-complete source: it carries both the simple entries
    // AND the parameterised `:id` / `:rest(.*)` variants that this plain
    // string map cannot express. Earlier these simple entries were duplicated
    // here too, but `redirectRoutes` register before the /admin block so the
    // duplicates always won `router.resolve` -- silently dead copies that
    // could diverge from index.js on edit. Reconciled: index.js is canonical.
    // (Bare `/admin` is also intentionally absent -- it resolves to the
    // SuperAdmin Dashboard sa_dashboard; a `'/admin': '/distributor'` entry
    // here would shadow it because redirectRoutes register first.)
};

export default REDIRECTS;
